import { Component, Input, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { TranslateService } from '@ngx-translate/core';
import { ProductDownloadService } from 'src/app/core/services/product-download.service';
import { ReportsService } from 'src/app/core/services/reports.service';
import { WorkFlowService } from 'src/app/core/services/work-flow.service';
import { ProductDto } from 'src/app/model/ProductDto';

@Component({
  selector: 'ms-reports',
  templateUrl: './reports.component.html',
  styleUrls: ['./reports.component.scss']
})
export class ReportsComponent implements OnInit {
  @Input() productDto: ProductDto = null;
  @Input() searchString: string = '';
  @Input() functionalArea: string = '';
  @Input() genericFilters: FormGroup = null;
  @Input() specificFilters: FormGroup = null;
  @Input() reportType: string;

  title: string = '';
  date: string = '';
  isLoading: boolean = false;

  data: object[] = [];
  filteredData: object[] = [];
  localizedFields: Map<string, string> = new Map<string, string>()
  functionalAreasTable: MatTableDataSource<string> = new MatTableDataSource<string>();
  functionalAreasDataTables: Map<string, MatTableDataSource<object>> = new Map<string, MatTableDataSource<object>>();
  expandedElement: string | null;

  constructor(
    private reportsService: ReportsService,
    private translateService: TranslateService,
    private productDownloadService: ProductDownloadService,
    private workFlow: WorkFlowService
  ) {}

  async ngOnInit(): Promise<void> {
    try{
      this.isLoading = true;

      this.localizedFields = this.reportsService.getLocalizedFields(this.workFlow.getLanguageForCms());

      if(this.productDto.ProductCode){
        this.date = new Date(this.productDto.Date).toLocaleDateString();
        let prod = await this.translateService.get('PRODUCTS').toPromise();
        this.title = `${prod[this.reportType]} ${this.productDto.Description} ${this.productDto.ReleaseNr}`;
      }

      let res = await this.getData(this.reportType);
      if(res.Result){
        this.data = res.Content;
        this.data = this.reportsService.setObjectsEmptyFields(this.data);
        if(this.functionalArea) this.data = this.data.filter(item => item[this.localizedFields.get('FunctionalAreas')].includes(this.functionalArea));
        this.setTables(this.reportType);
        this.genericFilters.valueChanges.subscribe(() => { this.setTables(this.reportType); })
        this.specificFilters.valueChanges.subscribe(() => { this.setTables(this.reportType); })
      }
      this.isLoading = false;
    } catch(err){
      this.isLoading = false;
    }

  }

  async getData(dataType: string){
    switch(dataType){
      case 'BUGS':
        return await this.productDownloadService.getProductBugs(this.productDto.ProductCode ?? "", this.productDto.ReleaseNr ?? "", this.searchString);
      case 'IMPROVEMENTS':
        return await this.productDownloadService.getProductImprovements(this.productDto.ProductCode ?? "", this.productDto.ReleaseNr ?? "", 0, this.searchString)
      case 'INCIDENTS':
        return await this.productDownloadService.getIncidents(this.productDto.ProductCode ?? "", this.productDto.ReleaseNr ?? "", this.searchString);
    }
  }

  setTables(dataType: string){
    if(this.genericFilters == null && this.specificFilters == null)
      this.filteredData = this.data
    else{
      this.filteredData = this.data.filter(item =>
        (!this.genericFilters.controls['start']?.value || new Date(item['RecordingDate']) >= new Date(this.genericFilters.controls['start'].value)) &&
        (!this.genericFilters.controls['end']?.value || new Date(item['RecordingDate']) <= new Date(this.genericFilters.controls['end'].value))
      )
      switch(dataType){
        case 'BUGS':
          this.filteredData = this.filteredData.filter(bug =>
            (!this.specificFilters?.controls['status']?.value || bug['BugsStatus'] === this.specificFilters.controls['status'].value) &&
            (!this.specificFilters?.controls['severity']?.value || bug['Severity'] === this.specificFilters.controls['severity'].value)
          )
          break;
        case 'IMPROVEMENTS':
          this.filteredData = this.filteredData.filter(improvement =>
            (!this.specificFilters?.controls['status']?.value || improvement['PIStatus'] === this.specificFilters.controls['status'].value)
          )
          break;
      }
    }

    this.functionalAreasDataTables.clear();
    this.functionalAreasTable.data = [];
    let functionalAreas = this.getAllFunctionalAreas(this.filteredData);
    this.functionalAreasTable.data = functionalAreas;
    functionalAreas.forEach((fa) => {
      let functionalAreaItems = this.filteredData.filter(item => item[this.localizedFields.get('FunctionalAreas')].includes(fa)) ?? []
      if(fa && functionalAreaItems)
        this.functionalAreasDataTables.set(fa, new MatTableDataSource<object>(functionalAreaItems));
    })
    if(functionalAreas.length == 1)
      this.expandedElement = this.functionalAreasTable.data[0]
  }

  getAllFunctionalAreas(data: object[]): string[]{
    let functionalAreas: string[] = [];
    data.forEach(item => {
      item[this.localizedFields.get('FunctionalAreas')].split(';').forEach(bug => {
        if(!functionalAreas.includes(bug))
          functionalAreas.push(bug)
      })
    })
    return functionalAreas;
  }
}
